import { addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, parse, isValid } from 'date-fns'
import { isValidTitle1Program } from './ExploreTable/types'

import {
  RELIGIOUS_AFFILIATION_OPTIONS,
  ReligiousAffiliation,
  HSLocation,
  isValidSchoolType,
  UIFilters,
  unknownLocation,
} from './types'
import { AvailabilityOption } from './components/FilterDrawer'
import { statesByAbbrev, territoriesByAbbrev } from '@scoir/states'
type Converter<T extends UIFilters> = {
  [K in keyof T]: URLStateConverter<T[K]>
}

export const encodeToURLParams = <T extends UIFilters, C extends Converter<T>>(
  filters: T,
  converters: C,
  initialParams?: URLSearchParams,
): URLSearchParams => {
  const filterParams = new URLSearchParams(initialParams)
  Object.keys(converters).forEach((key) => {
    const k = key as keyof T
    const conv = converters[k] as URLStateConverter<T[keyof T]>
    const result = conv.encode(filters[k])
    if (result !== undefined) {
      filterParams.delete(key)
      if (Array.isArray(result)) {
        result.forEach((value) => {
          filterParams.append(key, value)
        })
      } else {
        filterParams.set(key, result)
      }
    } else {
      filterParams.delete(key)
    }
  })
  return filterParams
}

export const decodeFromURLParams = <T extends Record<string, unknown>, C extends Converter<T>>(
  params: URLSearchParams,
  converters: C,
) => {
  const state = {} as T
  Object.keys(converters).forEach((key) => {
    const k = key as keyof T
    const conv = converters[k] as URLStateConverter<T[keyof T]>
    const paramVal = params?.getAll(key)
    const decoded = conv.decode(paramVal)
    if (decoded !== undefined) {
      state[k] = decoded
    }
  })
  return state
}
export type URLStateConverter<T> = {
  encode: (v: T) => string | string[] | undefined
  decode: (v?: string[]) => T | undefined
}

// first provides a helper to work with multi-value query params, when only one is expected
const first =
  <T>(fn: (val?: string) => T) =>
  (v?: string[]) => {
    if (v === undefined) return
    return fn(v[0])
  }
export const UIFilterConverters: {
  [TKey in keyof UIFilters]-?: URLStateConverter<UIFilters[TKey]>
} = {
  schoolType: {
    encode: (v: UIFilters['schoolType']) => v,
    decode: first((v?: string) => {
      if (isValidSchoolType(v)) return v
      return
    }),
  },
  isActive: {
    encode: (v: UIFilters['isActive']) => (v?.toString() ? v?.toString() : 'false'),
    decode: first((v?: string) => (v === undefined ? true : v === 'true' ? true : undefined)),
  },
  isTitle1Eligible: {
    encode: (v: UIFilters['isTitle1Eligible']) => v?.toString(),
    decode: first((v?: string) => {
      if (v === undefined) return undefined
      if (v === 'true') return true
      if (v === 'false') return false
      return undefined
    }),
  },
  virtual: {
    encode: (v: UIFilters['virtual']) => v?.toString(),
    decode: first((v?: string) => (v === undefined ? undefined : v === 'true')),
  },
  title1Program: {
    encode: (v: UIFilters['title1Program']) => v,
    decode: first((v?: string) => {
      if (isValidTitle1Program(v)) return v
      return
    }),
  },
  classSize: {
    encode: (v: UIFilters['classSize'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => i || '').join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  visitRange: {
    encode: (v: UIFilters['visitRange']) => {
      if (v?.type === 'custom') {
        return [v.startDate, v.endDate]
          .map((d) => {
            if (!d) return ''
            return format(d, 'yyyy-MM-dd')
          })
          .join(';')
      }
      return v?.type
    },
    decode: first((val?: string) => {
      if (!val) return
      if (val === 'this-week' || val === 'this-month' || val === 'next-month') {
        return { type: val, ...getVisitVailabilityFromOption(val) }
      }
      const [startDate, endDate] = val.split(';').map((i) => {
        const d = parse(i, 'yyyy-MM-dd', new Date())
        return isValid(d) ? d : undefined
      })
      if (startDate === undefined && endDate === undefined) return
      return { type: 'custom', startDate, endDate }
    }),
  },
  pctFreeReducedLunch: {
    encode: (v: UIFilters['pctFreeReducedLunch'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => i || '').join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  religiousAffiliation: {
    encode: (v: UIFilters['religiousAffiliation']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v.reduce((acc, affiliation) => {
        if (RELIGIOUS_AFFILIATION_OPTIONS.includes(affiliation as ReligiousAffiliation)) {
          acc.push(affiliation as ReligiousAffiliation)
        }
        return acc
      }, [] as ReligiousAffiliation[])
    },
  },
  text: {
    encode: (v: UIFilters['text']) => (v === '' ? undefined : v),
    decode: first((v?: string) => (v === '' ? undefined : v)),
  },
  locations: {
    encode: (v: UIFilters['locations']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v.reduce((acc, location) => {
        const state = statesByAbbrev[location as keyof typeof statesByAbbrev]
        const territory = territoriesByAbbrev[location as keyof typeof territoriesByAbbrev]
        if (state) acc.push(state.value as HSLocation)
        if (territory) acc.push(territory.value as HSLocation)
        if (location === unknownLocation) acc.push('')
        return acc
      }, [] as HSLocation[])
    },
  },
} as const

function getVisitVailabilityFromOption(option: AvailabilityOption | undefined): UIFilters['visitRange'] {
  const now = new Date()
  switch (option) {
    case 'this-week': {
      const startDate = startOfWeek(now)
      const endDate = endOfWeek(now)
      return { type: option, startDate, endDate }
    }
    case 'this-month': {
      const startDate = startOfMonth(now)
      const endDate = endOfMonth(now)
      return { type: option, startDate, endDate }
    }
    case 'next-month': {
      const nextMonth = addMonths(now, 1)
      const startDate = startOfMonth(nextMonth)
      const endDate = endOfMonth(nextMonth)
      return { type: option, startDate, endDate }
    }
    case 'custom':
      return { type: option }
    default:
      return undefined
  }
}
